import RootPageLayout from '@bumble/app/components/RootPageLayout';
import { theme } from '@bumble/app/theme';
import queryClient from '@bumble/app/utils/query-client';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useMemo, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Api } from './__generated__/Api';
import { ApiContext, MfaContext, MultiFactorAuthData, SecurityData } from './context';
import { SubscriberProvider } from './context/SubscriberContext';

const baseUrl = import.meta.env.VITE_API;
const api = new Api<SecurityData>({
  baseUrl,
  securityWorker: async (data) =>
    data
      ? {
        headers: {
          Authorization: `Bearer ${data.access_token}`,
        },
      }
      : {},
});

function App() {
  const [user, setUser] = useState<MultiFactorAuthData | null>(null);
  const value = useMemo(() => ({ user, setUser }), [user, setUser]);
  queryClient.setDefaultOptions({
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 1,
      onError: (error: any) => {
        if (error?.status === 401) {
          // eslint-disable-next-line no-console
          console.log('Account timed out, redirecting to logout');
          window.location.pathname = '/logout';
        }
      },
    },
  });
  return (
    <BrowserRouter>
      <SubscriberProvider>
        <MfaContext.Provider value={value}>
          <ApiContext.Provider value={api}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <CssBaseline />
                  <RootPageLayout />
                </LocalizationProvider>
              </ThemeProvider>
              {process.env.NODE_ENV === 'develop' && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
          </ApiContext.Provider>
        </MfaContext.Provider>
      </SubscriberProvider>
    </BrowserRouter>
  );
}

export default App;
